import { useEffect } from 'react';
import { ComponentPropsContext, SitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { GetServerSideProps } from 'next';

import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import Layout from 'src/Layout';
import NotFound from 'src/NotFound';
import { componentBuilder } from 'temp/componentBuilder';

import { FormPageProps } from '../types/formPageProps';
import { PublicEnvironmentConfig } from '../types/publicEnvironmentConfig';

import getConfig from '../temp/config';

const SitecorePage = ({
  notFound,
  componentProps,
  layoutData,
  headLinks,
}: SitecorePageProps): React.JSX.Element => {
  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  if (notFound || !layoutData.sitecore.route) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
      >
        <Layout layoutData={layoutData} headLinks={headLinks} />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// This function gets called at request time on server-side.
export const getServerSideProps: GetServerSideProps<FormPageProps> = async (context) => {
  const config = getConfig();
  const sitecorePageProps = await sitecorePagePropsFactory.create(context);
  const envConfig: PublicEnvironmentConfig = {
    googleRecaptchaV3SiteKey: config.googleRecaptchaV3SiteKey,
    storeApiKey: config.sitecoreApiKey,
    supportApiKey: config.sitecoreApiKeySupport,
    tieredServicePortalApiKey: config.sitecoreApiKeyTieredServicePortal,
    publicTieredServicePortalBaseUrl: config.publicTieredServicePortalBaseUrl,
  };

  return {
    props: { ...sitecorePageProps, envConfig },
    notFound: sitecorePageProps.notFound, // Returns custom 404 page with a status code of 404 when true
  };
};

export default SitecorePage;
